import {Decimal} from 'decimal.js';

import BenchlingConfig from 'BenchlingConfig';
import {MeasurementUnit} from 'constants/__generated/MeasurementUnit';
import {
  LegacyMeasurement,
  Measurement,
  MeasurementValidationResult,
  NonNullLegacyMeasurement,
  NonNullMeasurement,
  NULL_UNITS,
  NullLegacyMeasurement,
  NullMeasurement,
  ValidLegacyMeasurement,
} from 'constants/MeasurementConstants';
import {Container, ContainerSummary, getMaxSignificantFigures} from 'constants/SampleConstants';
import {SampleRestrictionStatus} from 'constants/SampleStatusConstants';
import * as InventoryUnitUtil from 'inventory/InventoryUnitUtil';
import {SampleControlsFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevel';
import {hasMinimumSCRFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevelUtil';
import {
  queryForContainerContents,
  queryInputContainerHasNameTemplate,
  queryTagSchemaHasContainerNameTemplate,
} from 'name-templates/ContainerNameTemplateUtil';
import getQuantityDisplayString from 'samples/getQuantityDisplayString';
import {getSampleRoleIds} from 'samples/SampleUtil';
import {
  ConfirmTransferQuantitiesData,
  ConfirmTransferQuantitiesDataRow,
  StorableModalsContainerTransfer,
} from 'samples/transfers/TransferModalUtil';

// NB: despite the identical signatures, this function differs from legacyUpdateQuantityUnits in that it
// supports custom units
export function updateQuantityUnits(quantity: LegacyMeasurement, newUnits: string | null): LegacyMeasurement {
  let newValue = null;
  if (quantity.value != null && quantity.units != null && newUnits != null && newUnits !== NULL_UNITS) {
    const oldUnit = InventoryUnitUtil.getUnitFromCacheBySymbol(quantity.units);
    const newUnit = InventoryUnitUtil.getUnitFromCacheBySymbol(newUnits);
    newValue = InventoryUnitUtil.convertToUnit({value: quantity.value, unit: oldUnit}, newUnit).value;
  }
  return {
    units: newUnits === NULL_UNITS ? null : newUnits,
    value: newValue,
  };
}

export function addMeasurementValues(value1: number | null, value2: number | null): number | null {
  if (value1 == null && value2 == null) {
    return null;
  } else {
    return (value1 || 0) + (value2 || 0);
  }
}

export function valueExceedsMaxSignificantFigures(value: number | null): boolean {
  return value != null && parseFloat(value.toPrecision(getMaxSignificantFigures())) !== value;
}

/*
 * Returns a display string for the measurement value, which is capped at getMaxSignificantFigures() digits
 * (both precision and total length).
 */
export function getMeasurementValueString(value: number): string {
  // Cap precision at getMaxSignificantFigures() to remove floating point arithmetic errors
  const fixedPrecisionValue = parseFloat(value.toPrecision(getMaxSignificantFigures()));
  if (String(fixedPrecisionValue).replace('.', '').length > getMaxSignificantFigures()) {
    return fixedPrecisionValue.toExponential();
  } else {
    return String(fixedPrecisionValue);
  }
}

/**
 * Returns a display string for a measurement
 *
 * Returns nullString (which defaults to 'Not specified') if there is no value.
 *
 * Keep in sync with benchling.inventory.inventory_util.get_measurement_string
 */
export function getLegacyMeasurementString(
  measurement?: LegacyMeasurement,
  nullString: string = 'Not specified'
): string {
  if (measurement == null || measurement.value == null) {
    return nullString;
  }
  return `${getMeasurementValueString(measurement.value)} ${measurement.units}`;
}

export function getMeasurementString(
  measurement?: Measurement | null,
  nullString: string = 'Not specified'
): string {
  if (measurement == null || measurement.value == null) {
    return nullString;
  }
  return `${getMeasurementValueString(measurement.value)} ${measurement.unit?.symbol ?? ''}`;
}

/**
 * Compare the value of two fully specified (non-null) measurements.
 *
 * Handles unit conversions.
 *
 * This behaves like a standard sorting compare function. It returns:
 *  - A positive number if a > b
 *  - 0 if a == b
 *  - A negative number if b > a
 */
export function compareNonNullMeasurements(a: NonNullMeasurement, b: NonNullMeasurement): number {
  // If the units are the same we can do a direct comparison, otherwise convert both measurements to standard and compare.
  if (a.unit.symbol === b.unit.symbol) {
    return a.value - b.value;
  }
  return InventoryUnitUtil.convertToStandard(a)!.cmp(InventoryUnitUtil.convertToStandard(b)!);
}

/**
 * Checks that a measurement's value is non-null and positive.
 */
export function isValidMeasurementValue(value: number | null): boolean {
  return value != null && value >= 0;
}

/**
 * Checks that a measurement's units is non-null.
 */
export function isValidMeasurementUnits(units: string | null): units is string {
  return units != null;
}

export function isValidMeasurementUnit(unit: MeasurementUnit | null): unit is MeasurementUnit {
  return unit != null;
}

/**
 * Type guard for measurements that are considered valid.
 *
 * Measurements are valid if they are:
 *  - Null (both value and units are null)
 *  - Fully specified (both value and units are not null and value > 0)
 */
export function isValidLegacyMeasurement(
  measurement?: LegacyMeasurement | null
): measurement is ValidLegacyMeasurement {
  return (
    isNullLegacyMeasurement(measurement) ||
    (measurement != null &&
      isValidMeasurementValue(measurement.value) &&
      isValidMeasurementUnits(measurement.units))
  );
}

export const INVALID_MEASUREMENT_VALUE_MESSAGE = 'Requires a non-negative numeric value.';
export const INVALID_MEASUREMENT_UNITS_MESSAGE = 'Requires a measuring unit.';

/**
 * Deprecated. Prefer validateMeasurement.
 */
export function validateLegacyMeasurement(
  measurement: LegacyMeasurement | null
): MeasurementValidationResult {
  const validation: MeasurementValidationResult = {valueError: null, unitsError: null};
  if (!isNullLegacyMeasurement(measurement)) {
    validation.valueError = isValidMeasurementValue(measurement!.value)
      ? null
      : INVALID_MEASUREMENT_VALUE_MESSAGE;
    validation.unitsError = isValidMeasurementUnits(measurement!.units)
      ? null
      : INVALID_MEASUREMENT_UNITS_MESSAGE;
  }
  return validation;
}

/**
 * Validates a measurement and returns an error string or null for the value and units of the measurement.
 */
export function validateMeasurement(measurement: Measurement | null): MeasurementValidationResult {
  const validation: MeasurementValidationResult = {valueError: null, unitsError: null};
  if (!isNullMeasurement(measurement)) {
    validation.valueError = isValidMeasurementValue(measurement!.value)
      ? null
      : INVALID_MEASUREMENT_VALUE_MESSAGE;
    validation.unitsError = isValidMeasurementUnit(measurement!.unit)
      ? null
      : INVALID_MEASUREMENT_UNITS_MESSAGE;
  }
  return validation;
}

/**
 * Returns true if all the units have the same symbol.
 *
 * Currently, does not check version number.
 */
export function unitsMatch(units: Array<MeasurementUnit | null>): boolean {
  return new Set(units.map((u) => u?.symbol)).size === 1;
}

/**
 * Returns true if all the measurements have the same value and units.
 *
 * Does not currently account for conversions. So, something like `[<1M>, <1000mM>]` will return false.
 */
export function measurementsMatch(measurements: Array<Measurement | null>): boolean {
  const values = measurements.map((measurement) => (measurement != null ? measurement.value : null));
  const units = measurements.map((measurement) => (measurement != null ? measurement.unit : null));
  return new Set(values).size === 1 && unitsMatch(units);
}

/**
 * Type guard for measurements that are not null (both value and units are not null).
 */
export function isNonNullLegacyMeasurement(
  measurement: LegacyMeasurement | null | undefined
): measurement is NonNullLegacyMeasurement {
  return measurement != null && measurement.value != null && measurement.units != null;
}

export function isNonNullMeasurement(
  measurement: Measurement | null | undefined
): measurement is NonNullMeasurement {
  return measurement != null && measurement.value != null && measurement.unit != null;
}

/**
 * Type guard for null measurements (both value and units are null).
 */
export function isNullLegacyMeasurement(
  measurement: LegacyMeasurement | null | undefined
): measurement is NullLegacyMeasurement {
  // We explicitly want value/unit === null and not undefined (although) the input types should make that impossible.
  // eslint-disable-next-line benchlint/no-eqeqeq-null
  return measurement != null && measurement.value === null && measurement.units === null;
}

export function isNullMeasurement(
  measurement: Measurement | null | undefined
): measurement is NullMeasurement {
  // We explicitly want value/unit === null and not undefined (although) the input types should make that impossible.
  // eslint-disable-next-line benchlint/no-eqeqeq-null
  return measurement != null && measurement.value === null && measurement.unit === null;
}

/**
 * Gets a single error string for a measurement, or null if there are no errors.
 */
export function getMeasurementError(measurement: LegacyMeasurement): string | null {
  const validation = validateLegacyMeasurement(measurement);
  // prioritize the value error over the unit error
  return validation.valueError ?? validation.unitsError;
}

export function validateContainerTransfer({
  finalOutputQuantityValue,
  transferQuantityValue,
  outputContainer,
}: ConfirmTransferQuantitiesDataRow): boolean {
  if (!outputContainer || !outputContainer.quantity) {
    return false;
  }
  if (!isValidLegacyMeasurement({value: finalOutputQuantityValue, units: outputContainer.quantity.units})) {
    return false;
  }
  if (finalOutputQuantityValue == null) {
    // Only allow the final quantity to be null if the output container already had null quantity
    return outputContainer.quantity.value == null && outputContainer.quantity.units == null;
  }
  return finalOutputQuantityValue >= (transferQuantityValue || 0) + (outputContainer.quantity.value || 0);
}

export type MinimalSourceContainerSummary = Pick<Container, 'id' | 'name' | 'quantity' | 'gridCell'>;

export interface MinimalSourceContainerSummaryWithContents extends MinimalSourceContainerSummary {
  containedItemIds: Array<string>;
}

export function getTransferQuantityValidationText(
  sourceContainer: MinimalSourceContainerSummary,
  isBlocking: boolean
): string {
  return isBlocking
    ? getBlockingTransferQuantityValidationErrorText(sourceContainer)
    : getNonBlockingTransferQuantityValidationWarningText(sourceContainer);
}

function getBlockingTransferQuantityValidationErrorText(
  sourceContainer: MinimalSourceContainerSummary
): string {
  return `The total ${getQuantityDisplayString(
    false,
    false
  )} you are trying to transfer exceeds what is available in the source container (${
    sourceContainer.name
  }: ${getLegacyMeasurementString(sourceContainer.quantity)}).`;
}

function getNonBlockingTransferQuantityValidationWarningText(
  sourceContainer: MinimalSourceContainerSummary
): string {
  const sourceSetToZeroMsg = `The full ${getQuantityDisplayString(
    false
  )} will be transferred, and the source container's ${getQuantityDisplayString(false)} will be set to 0.`;
  return `${getBlockingTransferQuantityValidationErrorText(sourceContainer)} ${sourceSetToZeroMsg}`;
}

/**
 * Extract a list of transfer quantity measurements from ConfirmTransferQuantitiesData.
 */
export function getTransferQuantitiesFromConfirmTransferQuantitiesData(
  confirmTransferQuantitiesData: ConfirmTransferQuantitiesData
): Array<LegacyMeasurement> {
  return confirmTransferQuantitiesData.map(({transferQuantityValue, outputContainer}) => ({
    value: transferQuantityValue,
    units: outputContainer.quantity.units,
  }));
}

export function validateAllTransferQuantities(
  sourcesWithQuantities: Array<{source: MinimalSourceContainerSummary; transferQuantity: LegacyMeasurement}>
): {
  isValid: boolean;
  sourcesInViolation: Array<MinimalSourceContainerSummary>;
  quantityErrorsAreBlocking: boolean;
} {
  const quantitiesBySource = sourcesWithQuantities.reduce((mapping, sourceWithQuantity) => {
    mapping.set(sourceWithQuantity.source, [
      ...(mapping.get(sourceWithQuantity.source) ?? []),
      sourceWithQuantity.transferQuantity,
    ]);
    return mapping;
  }, new Map<MinimalSourceContainerSummary, Array<LegacyMeasurement>>());
  const sourcesInViolation = Array.from(quantitiesBySource.entries())
    .filter(([source, quantities]) => !isTotalTransferQuantityValidForSource(source, quantities))
    .map(([source, _]) => source);
  return {
    isValid: sourcesInViolation.length === 0,
    sourcesInViolation,
    quantityErrorsAreBlocking: !BenchlingConfig.INVENTORY_DISABLE_ENFORCE_TRANSFER_QUANTITIES,
  };
}

function isTotalTransferQuantityValidForSource(
  sourceContainer: MinimalSourceContainerSummary,
  transferQuantities: Array<LegacyMeasurement>
): boolean {
  const {isValid} = getQuantitiesForTransferValidation(sourceContainer.quantity, transferQuantities);
  return isValid;
}

/**
 * Given a starting total quantity and a list of transfer quantities calculates:
 *  * Total transfer quantity (sum of a transfer quantities)
 *  * Remaining quantity (how much of the starting total quantity is remaining after the transfer)
 *  * If the transfer is valid (the total transfer quantity doesn't exceed the starting quantity)
 */
export function getQuantitiesForTransferValidation(
  startingTotalQuantity: LegacyMeasurement,
  transferQuantities: Array<LegacyMeasurement>
): {
  totalTransferQuantity: LegacyMeasurement;
  remainingQuantity: LegacyMeasurement;
  isValid: boolean;
  quantityErrorsAreBlocking: boolean;
} {
  const startingTotalQuantityUnit = startingTotalQuantity.units
    ? InventoryUnitUtil.getUnitFromCacheBySymbol(startingTotalQuantity.units)
    : null;
  const nonNullQuantities = transferQuantities
    .filter((q) => isNonNullLegacyMeasurement(q))
    .map(({value, units}: NonNullLegacyMeasurement) => ({
      value,
      unit: InventoryUnitUtil.getUnitFromCacheBySymbol(units),
    }));
  // Transfers with unspecified quantities / unspecified source quantity aren't considered
  // Quantity comparisons with incompatible units are meaningless; incompatible units are blocking and
  // handled elsewhere
  if (
    startingTotalQuantity.value == null ||
    startingTotalQuantityUnit == null ||
    !InventoryUnitUtil.areUnitsCompatible([
      startingTotalQuantityUnit,
      ...nonNullQuantities.map(({unit}) => unit),
    ])
  ) {
    return {
      remainingQuantity: startingTotalQuantity,
      totalTransferQuantity: {value: null, units: null},
      isValid: true,
      quantityErrorsAreBlocking: !BenchlingConfig.INVENTORY_DISABLE_ENFORCE_TRANSFER_QUANTITIES,
    };
  }
  const totalTransferQuantity = InventoryUnitUtil.convertFromStandard(
    nonNullQuantities.reduce(
      (sum, quantity) => Decimal.add(InventoryUnitUtil.convertToStandard(quantity)!, sum),
      new Decimal(0)
    ),
    startingTotalQuantityUnit
  );
  const remainingQuantity = {
    value: startingTotalQuantity.value - totalTransferQuantity.value!,
    units: startingTotalQuantity.units,
  };
  return {
    remainingQuantity,
    totalTransferQuantity: {
      value: totalTransferQuantity.value,
      units: totalTransferQuantity.unit?.symbol ?? null,
    },
    isValid: remainingQuantity.value >= 0,
    quantityErrorsAreBlocking: !BenchlingConfig.INVENTORY_DISABLE_ENFORCE_TRANSFER_QUANTITIES,
  };
}

export function getTransferInputContentsErrorText(source: {name: string}): string {
  return `Source container ${source.name} does not have any contents to transfer.`;
}

export function getMultipleTransferInputContentsErrorText(forFixedPlateWells: boolean = true): string {
  return `None of the ${forFixedPlateWells ? 'wells' : 'containers'} have any contents to transfer.`;
}

export function validateTransferInputContents(
  sources: Array<MinimalSourceContainerSummaryWithContents>,
  requireAllValid: boolean = false
): {
  contentsAreValid: boolean;
  sourcesInViolation: Array<MinimalSourceContainerSummaryWithContents>;
} {
  // By default returns true if any sources are valid; can be configured to require all sources to be valid.
  const sourcesInViolation = sources.filter((source) => !isSourceContainerValidForTransfer(source));
  const numInViolation = sourcesInViolation.length;
  const contentsAreValid = requireAllValid ? numInViolation === 0 : numInViolation < sources.length;
  return {contentsAreValid, sourcesInViolation};
}

export function isSourceContainerValidForTransfer(
  source: Pick<MinimalSourceContainerSummaryWithContents, 'containedItemIds'>
): boolean {
  return (
    !BenchlingConfig.INVENTORY_ENABLE_REQUIRE_CONTENTS_FOR_TRANSFERS || source.containedItemIds.length > 0
  );
}

export function isNonVolumeTransfer(
  transfer: StorableModalsContainerTransfer,
  inputContainer: {quantity: LegacyMeasurement} | null = null
): boolean {
  const inputSymbol = inputContainer?.quantity.units ?? null;
  const inputUnit = inputSymbol ? InventoryUnitUtil.getUnitFromCacheBySymbol(inputSymbol) : null;
  const transferSymbol = transfer.quantity.units;
  const transferUnit = transferSymbol ? InventoryUnitUtil.getUnitFromCacheBySymbol(transferSymbol) : null;
  const outputSymbol = transfer.outputContainer.quantity.units;
  const outputUnit = outputSymbol ? InventoryUnitUtil.getUnitFromCacheBySymbol(outputSymbol) : null;
  return (
    !InventoryUnitUtil.isVolumeOrNullUnit(inputUnit) ||
    !InventoryUnitUtil.isVolumeOrNullUnit(transferUnit) ||
    !InventoryUnitUtil.isVolumeOrNullUnit(outputUnit)
  );
}

export function willTransferModifyOutputSampleRoles(
  inputContainer: ContainerSummary | null,
  outputContainers: Array<ContainerSummary>
): boolean {
  if (inputContainer == null || !hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V1)) {
    return false;
  }

  const inputSampleOwnerIds = getSampleRoleIds(inputContainer.sampleRoles, 'sampleOwners');
  const inputRestrictedSampleUserIds = getSampleRoleIds(inputContainer.sampleRoles, 'restrictedSampleUsers');
  const inputRestrictionStatus = inputContainer.restrictionStatus;

  if (
    inputSampleOwnerIds.length === 0 &&
    inputRestrictedSampleUserIds.length === 0 &&
    inputRestrictionStatus !== SampleRestrictionStatus.RESTRICTED
  ) {
    return false;
  }

  for (const outputContainer of outputContainers) {
    const outputSampleOwnerIds = getSampleRoleIds(outputContainer.sampleRoles, 'sampleOwners');
    const outputRestrictedSampleUserIds = getSampleRoleIds(
      outputContainer.sampleRoles,
      'restrictedSampleUsers'
    );

    const newSampleOwners = new Set(
      inputSampleOwnerIds.filter((sampleOwner) => !outputSampleOwnerIds.includes(sampleOwner))
    );
    const newRestrictedSampleUsers = new Set(
      inputRestrictedSampleUserIds.filter(
        (restrictedSampleUser) => !outputRestrictedSampleUserIds.includes(restrictedSampleUser)
      )
    );
    const isChangingRestrictionStatus =
      inputRestrictionStatus === SampleRestrictionStatus.RESTRICTED &&
      outputContainer.restrictionStatus !== SampleRestrictionStatus.RESTRICTED;

    if (newSampleOwners.size > 0 || newRestrictedSampleUsers.size > 0 || isChangingRestrictionStatus) {
      return true;
    }
  }

  return false;
}

export async function willTransferApplyContainerNameTemplate(
  inputBioentityTagSchemaId: string | null,
  inputContainerId: string | null,
  outputContainers: Array<ContainerSummary>
): Promise<boolean> {
  const outputContainersWithContents = await queryForContainerContents(
    outputContainers.filter((container) => !container.isFixedPlateWell).map((container) => container.id)
  );
  // Must be at least one empty output container
  if (!outputContainersWithContents.some((container) => container.contents.length === 0)) {
    return false;
  }
  // Name template must be defined on input bioentity tag schema or tag schema of first contained entity in
  // input container
  return (
    (inputBioentityTagSchemaId != null &&
      (await queryTagSchemaHasContainerNameTemplate(inputBioentityTagSchemaId))) ||
    (inputContainerId != null && (await queryInputContainerHasNameTemplate(inputContainerId)))
  );
}
